import React from 'react'
import { Autocomplete, CircularProgress, MenuItem, TextField } from '@mui/material'
import { com } from '@eidu/form'
import EntityReference from '../data/EntityReference'
import EqualityHashMap from '../../util/EqualityHashMap'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityId = com.eidu.sharedlib.entity.EntityId
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString

export type EntityAutocompleteProps = {
  entitiesById: EqualityHashMap<EntityId, EntityWithLabelAndRelated>
  entityTypesById: ReadonlyMap<EntityTypeId, EntityType | null>
  value: EntityId | undefined
  onChange: (id: EntityId | undefined) => void
  onSearchChange: (query: string) => void
  loadMore?: () => void
  hasMore?: boolean
  loading?: boolean
  label: string
  required?: boolean
  error?: boolean
  disabled?: boolean
  noOptionsText?: string
}

const EntityAutocomplete = ({
  entitiesById,
  entityTypesById,
  value,
  onChange,
  onSearchChange,
  loadMore,
  hasMore = false,
  loading = false,
  label,
  required,
  error,
  disabled,
  noOptionsText = 'No entities',
}: EntityAutocompleteProps) => {
  const optionsBase = [...entitiesById.keys()].map((id) => id.toString())
  const options = value ? [value.toString(), ...optionsBase.filter((id) => id !== value.toString())] : optionsBase

  return (
    <Autocomplete
      disabled={disabled}
      options={options}
      getOptionLabel={(entityId) => {
        const entity = entitiesById.get(entityIdFromString(entityId))
        return entity?.primaryLabelText ?? ''
      }}
      loading={loading}
      value={value?.toString() || null}
      onChange={(_, newValue) => {
        if (!newValue) {
          onChange(undefined)
        } else {
          const parsed = entityIdFromString(newValue)
          onChange(parsed ?? undefined)
        }
      }}
      noOptionsText={noOptionsText}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') onSearchChange(newInputValue)
        if (reason === 'clear') onSearchChange('')
      }}
      ListboxProps={{
        onScroll: (event: React.UIEvent<HTMLUListElement>) => {
          const list = event.currentTarget
          if (hasMore && loadMore) {
            const nearBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 24
            if (nearBottom) loadMore()
          }
        },
        sx: {
          minWidth: 'fit-content',
          maxWidth: '80vw',
        },
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 'fit-content',
            maxWidth: '80vw',
          },
        },
      }}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          label={label}
          error={error}
          placeholder={loading ? ' ' : undefined}
          InputLabelProps={loading ? { shrink: true } : undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={30} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, entityId) => {
        const entity = entitiesById.get(entityIdFromString(entityId))
        const entityType = entity && entityTypesById.get(entity.entity.typeId)
        return (
          entity && (
            <MenuItem {...props} key={`entity-option-${entity.entity.id.toString()}`}>
              <EntityReference
                id={entityIdFromString(entityId)}
                typeId={entity.entity.typeId}
                label={entity.primaryLabelText}
                secondaryLabel={entity.secondaryLabelText}
                labelAsLink={false}
                typeName={entityType?.name}
              />
            </MenuItem>
          )
        )
      }}
    />
  )
}

export default EntityAutocomplete
